// The admin's view of one remote punch: the map first, then the area name in
// the viewer's language, the time, the accuracy and the coordinates, with a
// link out to Google Maps.
import React from "react";
import { MapPin, ExternalLink, Building2, Eye, Clock, Crosshair, Navigation } from "lucide-react";
import RemotePunchDialog, { dialogColors } from "./RemotePunchDialog";
import PunchMap from "./PunchMap";
import { rpText } from "./remotePunchText";
import { areaNameFor, googleMapsUrl } from "./remotePunchApi";
import { InfoTile, TileGrid, AccuracyValue, Coordinates, primaryLinkStyle, formatDateTime } from "./RemotePunchParts";
import { accuracyQuality } from "./PunchMap";

export default function PunchLocationModal({ punch, kind, employeeName, theme, language, onClose }) {
  if (!punch) return null;
  const t = rpText(language);
  const dark = theme === "dark";
  const c = dialogColors(dark);
  const area = areaNameFor(punch, language);
  const hasPosition = Number.isFinite(punch.latitude) && Number.isFinite(punch.longitude);
  const kindLabel = kind === "out" ? t.checkOut : t.checkIn;

  return (
    <RemotePunchDialog
      onClose={onClose}
      title={employeeName || t.pinTitle}
      subtitle={`${t.pinTitle} · ${kindLabel}`}
      icon={MapPin}
      iconColor={c.danger}
      dark={dark}
      language={language}
      maxWidth={640}
      closeLabel={t.close}
      footer={
        hasPosition && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <a href={googleMapsUrl(punch.latitude, punch.longitude)} target="_blank" rel="noopener noreferrer" style={primaryLinkStyle(c)}>
              <ExternalLink size={15} />
              {t.openInGoogleMaps}
            </a>
          </div>
        )
      }
    >
      {hasPosition && (
        <PunchMap
          latitude={punch.latitude}
          longitude={punch.longitude}
          accuracyMeters={punch.accuracyMeters}
          dark={dark}
          height={300}
          failedText={t.mapLoadFailed}
          recenterLabel={t.recenter}
          accuracyLabel={t.quality[accuracyQuality(punch.accuracyMeters).level]}
        />
      )}

      <div style={{ marginTop: 14 }}>
        <TileGrid>
          <InfoTile c={c} icon={Navigation} label={t.area} accent={c.primary} wide>
            <span style={{ fontSize: 16 }}>
              {area || <span style={{ color: c.muted, fontWeight: 500, fontSize: 14 }}>{t.areaPending}</span>}
            </span>
          </InfoTile>
          {punch.locationName && (
            <InfoTile c={c} icon={Building2} label={t.office} accent={c.success} wide>{punch.locationName}</InfoTile>
          )}
          <InfoTile c={c} icon={Clock} label={kindLabel}>{formatDateTime(punch.at, language, true)}</InfoTile>
          {Number.isFinite(punch.accuracyMeters) && (
            <InfoTile c={c} icon={Crosshair} label={t.accuracy}><AccuracyValue meters={punch.accuracyMeters} t={t} /></InfoTile>
          )}
          {hasPosition && (
            <InfoTile c={c} icon={MapPin} label={t.coordinates} wide>
              <Coordinates latitude={punch.latitude} longitude={punch.longitude} t={t} c={c} />
            </InfoTile>
          )}
        </TileGrid>
        {punch.monitoringMode && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 600, color: c.warning }}>
            <Eye size={14} /> {t.monitoringPunch}
          </div>
        )}
      </div>
    </RemotePunchDialog>
  );
}
